import { FC, HTMLAttributes } from "react";
import { useStyles } from "./DailyCard.style";
import GemBox from "./assets/gems_box_1x.webp";
import GemBox2x from "./assets/gems_box_2x.webp";
import GemBox3x from "./assets/gems_box_3x.webp";
import GemIcon from "../../../assets/gem_1x.webp";
import GemIcon2x from "../../../assets/gem_2x.webp";
import GemIcon3x from "../../../assets/gem_3x.webp";

import clsx from "classnames";
import { useTranslation } from "react-i18next";
import { LoginReward, LoginRewardStatus } from "@onmo/graphql/graphql";
import {
  Image,
  Icon,
  Typography,
  TypographyColor,
  TypographyVariant,
} from "../../../core";

interface IDailyCardProps {
  fullWidth?: boolean;
  reward: Partial<LoginReward>;
  day: number;
  active?: boolean;
}

export const DailyCard: FC<
  IDailyCardProps & HTMLAttributes<HTMLDivElement>
> = ({ fullWidth, reward, day, className, active, ...otherProps }) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const collected =
    reward.claimStatus !== LoginRewardStatus.Unavailable && !active;

  if (fullWidth)
    return (
      <div
        {...otherProps}
        className={clsx(classes.dailyCard, className, { fullWidth, active })}
      >
        <div className={clsx(classes.rewardDay, { fullWidth })}>
          <div
            className={clsx(classes.fullWidthTitle, {
              unavailable: !collected && !active,
            })}
          >
            <Typography
              variant={TypographyVariant.BodyB5Bold}
              color={TypographyColor.Text1}
            >
              {t("common_Day")} {day}
            </Typography>
            {active && (
              <Icon
                name="icon-bold-tick-circle"
                className={clsx(classes.icon, "fullWidth", { active })}
              />
            )}
          </div>
          <Typography
            variant={TypographyVariant.BodyB2Bold}
            color={
              collected && !active
                ? TypographyColor.Text2
                : TypographyColor.Text1
            }
            className={classes.fullWidthGem}
          >
            <Image
              className={clsx(classes.gemIcon)}
              src={GemIcon}
              src2x={GemIcon2x}
              src3x={GemIcon3x}
            />
            &nbsp;
            {reward.amount}
          </Typography>
        </div>
        <div className={clsx(classes.rewardContent, { fullWidth, active })}>
          <Image
            id="closeTreasureIcon"
            className={classes.treasureIcon}
            src={GemBox}
            src2x={GemBox2x}
            src3x={GemBox3x}
          />
        </div>
      </div>
    );

  return (
    <div
      {...otherProps}
      className={clsx(classes.dailyCard, className, { active, collected })}
    >
      <div
        className={clsx(classes.rewardDay, {
          unavailable: !collected && !active,
        })}
      >
        <Typography
          variant={TypographyVariant.BodyB5Bold}
          color={collected ? TypographyColor.Text2 : TypographyColor.Text1}
        >
          {t("common_Day")} {day}
        </Typography>
        {(collected || active) && (
          <Icon
            name="icon-bold-check-only"
            size={12}
            className={clsx(classes.icon, { active })}
          />
        )}
      </div>
      <div className={clsx(classes.rewardContent, { active, collected })}>
        <Image
          className={clsx(classes.gemIcon, { collected })}
          src={GemIcon}
          src2x={GemIcon2x}
          src3x={GemIcon3x}
        />
        <Typography
          variant={TypographyVariant.BodyB2Bold}
          color={
            collected && !active ? TypographyColor.Text2 : TypographyColor.Text1
          }
        >
          {reward.amount}
        </Typography>
      </div>
    </div>
  );
};
